import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Send, FileText, Trash2, X, ArrowLeft } from "@/components/icons";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { formatEuro, formatNlDate } from "@/components/Badges";
import { Page } from "@/components/PageConsole";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InlineError } from "@/components/ListState";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_LABEL = { concept: "Concept", uitgegeven: "Uitgegeven", verstuurd: "Verstuurd" };

export default function FactuurDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);

  const load = async () => {
    setError("");
    try { const { data } = await api.get(`/facturen/${id}`); setF(data); }
    catch (e) { setError(getErrorMessage(e, "Kan factuur niet laden.")); }
  };
  useEffect(() => { load(); }, [id]);

  const isConcept = f && f.status === "concept";
  const isCredit = f && f.type === "creditnota";

  const removeWerkbon = async (wid) => {
    const rest = (f.regels || []).map((b) => b.werkbon_id).filter((x) => x !== wid);
    setBusy(true);
    try { const { data } = await api.put(`/facturen/${id}`, { werkbon_ids: rest }); setF(data); }
    catch (e) { toast.error(getErrorMessage(e, "Bijwerken mislukt.")); }
    finally { setBusy(false); }
  };

  const issue = async () => {
    setBusy(true);
    try { await api.post(`/facturen/${id}/issue`); toast.success("Factuur uitgegeven"); setIssueOpen(false); await load(); }
    catch (e) { toast.error(getErrorMessage(e, "Uitgeven mislukt.")); }
    finally { setBusy(false); }
  };

  const discard = async () => {
    setBusy(true);
    try { await api.delete(`/facturen/${id}`); toast.success("Concept verwijderd"); navigate("/facturen"); }
    catch (e) { toast.error(getErrorMessage(e, "Verwijderen mislukt.")); setBusy(false); }
  };

  const openPdf = async () => {
    try {
      const res = await api.get(`/facturen/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(getErrorMessage(e, "PDF openen mislukt.")); }
  };

  const send = async () => {
    setBusy(true);
    try { await api.post(`/facturen/${id}/send`); toast.success("Factuur verstuurd"); await load(); }
    catch (e) { toast.error(getErrorMessage(e, "Versturen mislukt.")); }
    finally { setBusy(false); }
  };

  const createCredit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/facturen/${id}/creditnota`, {}); toast.success("Creditnota-concept aangemaakt"); setCreditOpen(false); navigate(`/facturen/${data.id}`); }
    catch (e) { toast.error(getErrorMessage(e, "Creditnota maken mislukt.")); }
    finally { setBusy(false); }
  };

  if (error) return <Page console={{ variant: "view", title: "Factuur", backSlot: (<button data-testid="factuur-back-button" className="-ml-1 text-white" onClick={() => navigate("/facturen")}><ArrowLeft size={18} /></button>) }}><InlineError testid="factuur-load-error" message={error} onRetry={load} /></Page>;
  if (!f) return <Page console={{ variant: "view", title: "Factuur", backSlot: (<button data-testid="factuur-back-button" className="-ml-1 text-white" onClick={() => navigate("/facturen")}><ArrowLeft size={18} /></button>) }}><p className="text-body text-muted-foreground">Laden...</p></Page>;

  const actions = [];
  if (isConcept) {
    actions.push({ key: "issue", label: isCredit ? "Creditnota uitgeven" : "Uitgeven", variant: "primary", icon: FileText, onClick: () => setIssueOpen(true) });
    actions.push({ key: "pdf", label: "Concept-PDF", icon: Download, onClick: openPdf });
    actions.push({ key: "delete", label: "Verwijderen", icon: Trash2, onClick: () => setDeleteOpen(true) });
  } else {
    actions.push({ key: "pdf", label: "Download PDF", variant: "primary", icon: Download, onClick: openPdf });
    if (!isCredit) actions.push({ key: "send", label: "Versturen", icon: Send, onClick: send });
    if (!isCredit) actions.push({ key: "credit", label: "Creditnota maken", icon: FileText, onClick: () => setCreditOpen(true) });
  }

  const title = `${isCredit ? "Creditnota" : "Factuur"} ${f.factuur_nummer || "(concept)"}`;
  const facts = [f.entity_name || (f.entity_snapshot || {}).name, STATUS_LABEL[f.status] || f.status].filter(Boolean);

  return (
    <Page console={{ variant: "view", title, facts, actions, backSlot: (<button data-testid="factuur-back-button" className="-ml-1 text-white" onClick={() => navigate("/facturen")}><ArrowLeft size={18} /></button>) }}>
      <div className="space-y-4" data-testid="factuur-detail">
        <Card className="p-5">
          <div className="grid grid-cols-2 gap-3 text-body sm:grid-cols-4">
            <div><div className="text-label uppercase text-muted-foreground">Periode</div><div>{formatNlDate(f.periode_van)} – {formatNlDate(f.periode_tot)}</div></div>
            {f.factuurdatum && <div><div className="text-label uppercase text-muted-foreground">Factuurdatum</div><div>{formatNlDate(f.factuurdatum)}</div></div>}
            {f.vervaldatum && <div><div className="text-label uppercase text-muted-foreground">Vervaldatum</div><div>{formatNlDate(f.vervaldatum)}</div></div>}
            {isCredit && f.corrigeert_factuur_nummer && <div><div className="text-label uppercase text-muted-foreground">Creditnota bij</div><div className="font-mono">{f.corrigeert_factuur_nummer}</div></div>}
          </div>
        </Card>

        {(f.regels || []).map((b) => (
          <Card key={b.werkbon_id} className="p-5" data-testid={`factuur-block-${b.werkbon_id}`}>
            <div className="mb-2 flex items-start justify-between gap-3">
              <div className="text-body font-semibold text-foreground"><span className="font-mono">{b.werkbon_number}</span> — {b.omschrijving}{b.location_name ? ` (${b.location_name})` : ""}</div>
              {isConcept && (
                <Button variant="ghost" size="icon" data-testid={`factuur-remove-${b.werkbon_id}`} disabled={busy} onClick={() => removeWerkbon(b.werkbon_id)} title="Van de factuur halen"><X size={18} /></Button>
              )}
            </div>
            <div className="divide-y divide-border text-body">
              {(b.uren || []).map((u, i) => (
                <div key={`u${i}`} className="flex items-baseline justify-between gap-3 py-1.5">
                  <span>{u.uurtype_label} — {String(u.aantal).replace(".", ",")} × {formatEuro(u.tarief)}</span>
                  <span className="font-mono tabular-nums">{formatEuro(u.bedrag)}</span>
                </div>
              ))}
              {(b.materiaal || []).map((m, i) => (
                <div key={`m${i}`} className="flex items-baseline justify-between gap-3 py-1.5">
                  <span>{m.artikelnummer ? `${m.artikelnummer} — ` : ""}{m.part_naam} ({String(m.aantal).replace(".", ",")} × {formatEuro(m.stukprijs)})</span>
                  <span className="font-mono tabular-nums">{formatEuro(m.bedrag)}</span>
                </div>
              ))}
              {b.extern != null && <div className="flex justify-between py-1.5"><span>Extern{b.external_party ? ` — ${b.external_party}` : ""}</span><span className="font-mono tabular-nums">{formatEuro(b.extern)}</span></div>}
              {b.legacy_arbeid != null && <div className="flex justify-between py-1.5"><span>Arbeid (overgenomen uit reparatie)</span><span className="font-mono tabular-nums">{formatEuro(b.legacy_arbeid)}</span></div>}
              {b.legacy_materiaal != null && <div className="flex justify-between py-1.5"><span>Materiaal (overgenomen uit reparatie)</span><span className="font-mono tabular-nums">{formatEuro(b.legacy_materiaal)}</span></div>}
              <div className="flex justify-between py-1.5 font-semibold text-foreground"><span>Subtotaal werkbon</span><span className="font-mono tabular-nums">{formatEuro(b.subtotaal_ex_btw)}</span></div>
            </div>
          </Card>
        ))}

        {isConcept && (f.geblokkeerd || []).length > 0 && (
          <Card className="p-5" data-testid="factuur-detail-geblokkeerd">
            <h2 className="mb-2 text-body font-semibold text-foreground">Niet op deze factuur</h2>
            <ul className="space-y-1 text-data text-muted-foreground">
              {f.geblokkeerd.map((g) => <li key={g.werkbon_id}><span className="font-mono">{g.werkbon_number}</span> — {g.reden}</li>)}
            </ul>
          </Card>
        )}

        <Card className="p-5">
          <div className="space-y-1 text-body">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotaal ex btw</span><span className="font-mono tabular-nums" data-testid="factuur-subtotaal">{formatEuro(f.subtotaal_ex_btw)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">BTW {f.btw_percentage}%</span><span className="font-mono tabular-nums" data-testid="factuur-btw">{formatEuro(f.btw_bedrag)}</span></div>
            <div className="flex justify-between text-kop font-semibold text-foreground"><span>Totaal incl. btw</span><span className="font-mono tabular-nums" data-testid="factuur-totaal">{formatEuro(f.totaal_incl_btw)}</span></div>
          </div>
        </Card>
      </div>

      <AlertDialog open={issueOpen} onOpenChange={setIssueOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{isCredit ? "Creditnota uitgeven?" : "Factuur uitgeven?"}</AlertDialogTitle>
            <AlertDialogDescription>
              Er wordt nu een definitief nummer toegekend. Een uitgegeven {isCredit ? "creditnota" : "factuur"} kan niet meer gewijzigd of verwijderd worden — corrigeren doe je met een creditnota.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Terug</AlertDialogCancel>
            <AlertDialogAction data-testid="factuur-issue-confirm" disabled={busy} onClick={issue}>Uitgeven</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Concept verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>Dit concept heeft nog geen nummer, dus er ontstaat geen gat in de reeks.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Terug</AlertDialogCancel>
            <AlertDialogAction data-testid="factuur-delete-confirm" disabled={busy} onClick={discard}>Verwijderen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={creditOpen} onOpenChange={setCreditOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Creditnota maken?</AlertDialogTitle>
            <AlertDialogDescription>Er wordt een creditnota-concept aangemaakt voor alle werkbonnen op deze factuur. De oorspronkelijke factuur blijft ongewijzigd.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Terug</AlertDialogCancel>
            <AlertDialogAction data-testid="factuur-creditnota-confirm" disabled={busy} onClick={createCredit}>Creditnota maken</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
